import { useQueries } from '@tanstack/react-query';
import { getProductAvailability } from '@/api/ticketSchedule.api';
import { summarizeAvailability, type AvailabilitySummary } from '@/lib/slotAvailability';
import type { Product } from '@/types/product';

/**
 * Availability verdict per product for the visit date, keyed by product id.
 *
 * Shared by the barcode scanner (and any non-card consumer) so a scanned
 * sold-out / expired ticket is rejected the same way the catalog card disables
 * it. Slot-based tickets resolve from the day's schedule; non-slot tickets from
 * `stock`/`soldOut`. A `null` entry means "still loading / unknown" — callers
 * must NOT block on it (never reject a sale because a fetch is mid-flight),
 * mirroring the card's behavior. Query keys match `useAvailability`, so the
 * per-card fetches and this hook share one request + cache entry.
 */
export function useCatalogAvailability(
  products: Product[],
  visitDate: string,
): Record<string, AvailabilitySummary | null> {
  const slotBased = products.filter((p) => p.requiresSlot === true);

  const results = useQueries({
    queries: slotBased.map((p) => ({
      queryKey: ['availability', p.id, visitDate],
      queryFn: () => getProductAvailability(p.id, visitDate),
      enabled: Boolean(p.id) && Boolean(visitDate),
      staleTime: 15_000,
    })),
  });

  const map: Record<string, AvailabilitySummary | null> = {};

  // Slot-based verdicts from the schedule fetch.
  slotBased.forEach((p, i) => {
    const q = results[i];
    if (!q || q.isLoading || !q.data) {
      map[p.id] = null; // unknown yet — don't block
    } else if (q.isError) {
      map[p.id] = { state: 'available', remaining: null }; // never block on a fetch error
    } else {
      map[p.id] = summarizeAvailability(q.data.slots, { visitDate });
    }
  });

  // Non-slot verdicts from stock/soldOut.
  for (const p of products) {
    if (p.requiresSlot === true) continue;
    const stock = p.stock;
    const full = p.soldOut === true || stock === 0;
    map[p.id] = {
      state: full ? 'unavailable' : 'available',
      reason: full ? 'full' : undefined,
      remaining: typeof stock === 'number' ? stock : null,
    };
  }

  return map;
}
